/**
 * CopilotPanelComponent - Persistent copilot sidebar using the generic StreamingChatPanelComponent.
 *
 * Extends StreamingChatPanelBase for shared streaming state management and provides:
 * - Persistent sidebar that survives navigation (lives at app shell level)
 * - Page-context-aware conversations (via CopilotContextService)
 * - Sub-agent delegation with real-time progress (filter, automation, rewrite)
 *
 * For alpha: in-memory conversation history, no backend persistence.
 */

import { CommonModule, NgComponentOutlet } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  Type,
  ViewChild,
} from '@angular/core';
import { Router } from '@angular/router';
import { IonicModule } from '@ionic/angular';
import { Subscription } from 'rxjs';

import { AnalyticsService } from '../../../services/analytics.service';
import { type CopilotContextPayload, CopilotContextService } from '../../../services/copilot-context.service';
import { CopilotNotificationsService, presentedNotificationIds } from '../../../services/copilot-notifications.service';
import { CopilotService, CopilotStreamCallbacks } from '../../../services/copilot.service';
import { ToolProvidersService } from '../../../services/external-tool-providers.service';
import { MessageService } from '../../../services/message.service';
import { BaseStreamCallbacks } from '../../../services/streaming-chat.service';
import {
  BaseAssistantMessage,
  ContentPart,
  HitlApprovalInfo,
  MessageActionClickEvent,
  StreamingChatMessageAction,
  StreamingChatEmptyState,
  StreamingChatPanelBase,
  StreamingChatPanelComponent,
  StreamingChatPanelConfig,
  SubAgentProgress,
} from '../streaming-chat-panel';
import type { SendMessagePayload } from '../streaming-chat-panel/streaming-chat-panel.types';

import { GENERAL_COPILOT_PRESETS, getCopilotPresetsForContext } from './copilot-panel-presets';
import { CopilotTurnScheduler, type StartedTurn } from './copilot-turn-scheduler';

const UNCONFIRMED_APPROVAL_MESSAGE =
  'The action may already have run, but its result could not be confirmed. Please start a new chat.';

/** Tool calls hidden from UI — delegation tools are represented by their sub-agent events instead */
const HIDDEN_TOOL_CALLS = new Set<string>([
  'run_automation_assistant',
  'update_conversation_filters',
  'run_text_rewrite',
  'insert_text_at_cursor',
  'propose_kb_article_edit',
  'run_analytics_assistant',
  'run_navigation_assistant',
]);

/** Extended message with copilot-specific data */
interface CopilotMessage extends BaseAssistantMessage {}

@Component({
  selector: 'app-copilot-panel',
  templateUrl: './copilot-panel.component.html',
  styleUrls: ['./copilot-panel.component.scss'],
  standalone: true,
  imports: [CommonModule, NgComponentOutlet, IonicModule, StreamingChatPanelComponent],
})
export class CopilotPanelComponent
  extends StreamingChatPanelBase<CopilotMessage>
  implements OnInit, OnChanges, OnDestroy
{
  @ViewChild(StreamingChatPanelComponent) private chatPanel?: StreamingChatPanelComponent;

  // ============================================================================
  // Inputs
  // ============================================================================

  @Input() isOpen = false;

  // ============================================================================
  // Outputs
  // ============================================================================

  @Output() panelClose = new EventEmitter<void>();

  // ============================================================================
  // Context Display
  // ============================================================================

  get contextDisplayComponent(): Type<unknown> | undefined {
    return this.copilotContext.contextDisplayComponent;
  }

  // ============================================================================
  // Chat Panel Configuration
  // ============================================================================

  chatConfig: StreamingChatPanelConfig = {
    placeholder: 'Ask Tailor to help…',
    modelHint: 'Claude by Anthropic',
    getToolCallLabel: (toolName, input) => this.getToolCallLabel(toolName, input),
    shouldShowToolCall: (toolName) => !HIDDEN_TOOL_CALLS.has(toolName),
    hideInputActions: true,
  };

  private contextDisplaySub?: Subscription;
  private contextDisplayContext: CopilotContextPayload | null = null;

  emptyState: StreamingChatEmptyState = {
    title: 'Tailor',
    badge: '',
    description:
      'Your Unthread AI assistant in the dashboard. Ask about tickets and analytics, search the knowledge base, trigger automations, and move faster on whatever page you are on.',
    presets: GENERAL_COPILOT_PRESETS,
    keyboardShortcut: 'Shift + /',
  };

  // ============================================================================
  // Constructor
  // ============================================================================

  constructor(
    private messageSvc: MessageService,
    private copilotContext: CopilotContextService,
    private copilotService: CopilotService,
    private externalToolProvidersService: ToolProvidersService,
    private router: Router,
    private analyticsService: AnalyticsService,
    private copilotNotifications: CopilotNotificationsService,
  ) {
    super();
  }

  /** Notifications the request in flight carries; marked seen when its turn finishes. */
  private presentedNotificationIds: string[] = [];
  /** Notifications a turn presented while the panel was closed: seen once the panel opens on them. */
  private presentedWhileClosed: string[] = [];
  /** Who has Tailor's one turn slot, and who gets it next (see `CopilotTurnScheduler`). */
  private readonly turns = new CopilotTurnScheduler();
  private unseenNotificationIds: string[] = [];
  private notificationsSub?: Subscription;

  private get turnState() {
    return { open: this.isOpen, unseenIds: this.unseenNotificationIds };
  }

  /**
   * A request settled and cleaned up: the slot passes straight to the next
   * turn. A turn waiting on an approval card keeps it — the resumed reply is
   * still that turn.
   */
  protected override startNextTurn(): void {
    if (this.pendingHitlApproval) {
      if (this.turns.awaitingApproval()) {
        return;
      }

      // The turn was stopped, or its chat replaced: no card is left to answer.
      this.pendingHitlApproval = false;
    }

    void this.begin(this.turns.turnEnded(this.turnState));
  }

  /** Tailor is free: bring up what Copilot noticed, if it should. */
  private startIdleTurn(): void {
    if (!this.isLoading && !this.pendingHitlApproval) {
      void this.begin(this.turns.idle(this.turnState));
    }
  }

  private begin(turn: StartedTurn | null): Promise<void> {
    if (!turn) {
      return Promise.resolve();
    }

    if (turn.kind === 'user') {
      this.queuedMessage = undefined;
    }

    return this.sendRequest(turn.message, { hidden: turn.hidden, images: turn.images });
  }

  /** What the user sends goes now when Tailor is free, otherwise waits — all of it, images included — as their next turn. */
  override async onSendMessage(payload: SendMessagePayload): Promise<void> {
    const text = payload.text.trim();

    if (!text && !payload.images?.length) {
      return;
    }

    const turn = this.turns.userSends({ text, images: payload.images });

    if (turn) {
      await this.begin(turn);
    } else {
      // The queued-message indicator shows what is waiting.
      this.queuedMessage = text || 'Image';
    }
  }

  /** A send the scheduler did not start (a retry of the last message) still owns the slot while it runs. */
  protected override async sendRequest(
    message: string,
    options?: { hidden?: boolean; images?: string[] },
  ): Promise<void> {
    this.turns.adopt();
    await super.sendRequest(message, options);
  }

  /**
   * A new chat: nothing from the old one carries over, an approval card it was
   * waiting on included. An approval still saving a key or waiting on its
   * sign-in popup was stopped first (`onNewChat`), so it touches nothing.
   */
  protected override resetDomainState(): void {
    this.turns.reset();
    this.pendingHitlApproval = false;
    this.sessionUnavailable = false;
  }

  override onClearQueuedMessage(): void {
    this.turns.clearQueuedUser();
    super.onClearQueuedMessage();
  }

  override onCancelRequest(): void {
    // Stop means stop: nothing that arrives for this turn counts, and nothing
    // automatic follows — only a message the user queued.
    this.turns.stop();
    super.onCancelRequest();
  }

  // ============================================================================
  // Abstract Member Implementations
  // ============================================================================

  protected get chatPanelRef(): StreamingChatPanelComponent | undefined {
    return this.chatPanel;
  }

  protected get messageService(): MessageService {
    return this.messageSvc;
  }

  get panelCloseEmitter(): EventEmitter<void> {
    return this.panelClose;
  }

  onConversationLinkClick(event: MouseEvent): void {
    if (event.defaultPrevented || event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) {
      return;
    }

    const link = event.target instanceof Element ? event.target.closest('a') : null;
    const href = link?.getAttribute('href');

    if (!href?.startsWith('/dashboard/conversations/') || link?.hasAttribute('download') || link?.target === '_blank') {
      return;
    }

    event.preventDefault();
    void this.router.navigateByUrl(href);
  }

  protected getHiddenToolCalls(): Set<string> {
    return HIDDEN_TOOL_CALLS;
  }

  getToolCallLabel(toolName: string, input?: unknown): string {
    const inputObj = input as Record<string, unknown> | undefined;

    switch (toolName) {
      case 'web_search': {
        const query = inputObj?.query as string;

        if (query) {
          const truncated = query.length > 40 ? query.slice(0, 40) + '...' : query;

          return `Searching: "${truncated}"`;
        }

        return 'Searching the web';
      }

      case 'web_fetch': {
        const url = inputObj?.url as string;

        if (url) {
          try {
            const urlObj = new URL(url);
            const display = urlObj.hostname + urlObj.pathname;
            const truncated = display.length > 40 ? display.slice(0, 40) + '...' : display;

            return `Fetching: ${truncated}`;
          } catch {
            const truncated = url.length > 40 ? url.slice(0, 40) + '...' : url;

            return `Fetching: ${truncated}`;
          }
        }

        return 'Fetching webpage';
      }

      case 'run_navigation_assistant':
        return 'Navigating';

      case 'update_conversation_filters':
        return 'Updating filters';

      case 'run_automation_assistant':
        return 'Modifying automation';

      case 'run_text_rewrite':
        return 'Rewriting text';

      case 'insert_text_at_cursor':
        return 'Inserting text';

      case 'propose_kb_article_edit':
        return 'Editing article';

      case 'run_analytics_assistant':
        return 'Analyzing data';

      case 'draft_knowledge_base_article':
        return 'Drafting KB article';

      default:
        return toolName.replace(/_/g, ' ');
    }
  }

  protected getMessageActions(msg: CopilotMessage, isLastMessage: boolean): StreamingChatMessageAction[] {
    const actions: StreamingChatMessageAction[] = [];

    // Resend/Delete actions for orphaned user message after cancellation
    if (this.wasCancelled && msg.role === 'user' && isLastMessage && !this.isLoading) {
      actions.push(
        {
          id: 'resend',
          label: 'Resend',
          icon: 'arrow-redo-outline',
          color: 'primary',
          fill: 'outline',
        },
        {
          id: 'delete_message',
          label: 'Delete',
          icon: 'trash-outline',
          color: 'danger',
          fill: 'outline',
        },
      );
    }

    if (msg.isError && isLastMessage && !this.isLoading && this.sessionUnavailable) {
      actions.push({ id: 'new_chat', label: 'New chat', icon: 'add-outline', color: 'primary', fill: 'solid' });

      return actions;
    }

    // Retry action for error messages
    if (msg.isError && msg.role === 'assistant' && isLastMessage && !this.isLoading && this.lastUserMessage) {
      actions.push({
        id: 'retry',
        label: 'Retry',
        icon: 'refresh-outline',
        color: 'primary',
        fill: 'solid',
      });
    }

    return actions;
  }

  /** Tracks whether a HITL approval is pending (disables input). */
  pendingHitlApproval = false;
  private sessionUnavailable = false;

  protected override resetPerRequestState(): void {
    this.sessionUnavailable = false;
  }

  private oauthPopupPollInterval: ReturnType<typeof setInterval> | null = null;

  protected onStreamDone(response: unknown): void {
    // The turn showed what its request carried — exactly those, so one raised
    // while it ran stays unseen. Seen means on screen: with the panel closed,
    // they count as seen once it opens on them.
    if (this.isOpen) {
      this.copilotNotifications.markSeen(this.presentedNotificationIds);
    } else {
      this.presentedWhileClosed.push(...this.presentedNotificationIds);
    }

    this.presentedNotificationIds = [];

    const res = response as {
      hitlAction?: {
        approvalId?: string;
        type: string;
        tools?: Array<{ toolName: string; toolArgs: Record<string, unknown>; toolDescription: string }> | null;
        toolName?: string;
        toolDescription?: string;
        toolArgs?: Record<string, unknown>;
        oauthConnectionName?: string;
        oauthConnectionId?: string;
      };
      runId?: string;
    };

    if (res.hitlAction && res.runId) {
      this.pendingHitlApproval = true;

      // Add hitl-approval content part to the last assistant message
      const lastMsg = this.conversationHistory[this.conversationHistory.length - 1];

      if (lastMsg?.role === 'assistant') {
        const parts = [...(lastMsg.contentParts ?? [])];
        const approval: HitlApprovalInfo = {
          approvalId: res.hitlAction.approvalId,
          tools: res.hitlAction.tools ?? null,
          toolName: res.hitlAction.toolName ?? 'Unknown tool',
          toolDescription: res.hitlAction.toolDescription,
          toolArgs: res.hitlAction.toolArgs,
          type:
            res.hitlAction.type === 'oauth_required'
              ? 'oauth'
              : res.hitlAction.type === 'api_key_required'
                ? 'api_key'
                : 'tool_approval',
          oauthConnectionName: res.hitlAction.oauthConnectionName,
          oauthConnectionId: res.hitlAction.oauthConnectionId,
          runId: res.runId,
        };

        parts.push({ type: 'hitl-approval', approval });
        lastMsg.contentParts = parts;
        // Trigger change detection
        this.conversationHistory = [...this.conversationHistory];
      }
    }
  }

  // ============================================================================
  // HITL Approval Handling
  // ============================================================================

  async onHitlApprovalClick(approval: HitlApprovalInfo, approved: boolean, apiKey?: string): Promise<void> {
    if (
      approval.resolved ||
      approval.invalidated ||
      approval.answering ||
      this.isLoading ||
      this.sessionUnavailable ||
      !this.sessionId
    ) {
      return;
    }

    // One answer at a time: Stop can't recall a key save or a sign-in setup,
    // so the card stays closed until the answer it cut short has finished.
    approval.answering = true;
    this.conversationHistory = [...this.conversationHistory];

    try {
      await this.answerApproval(approval, approved, apiKey);
    } finally {
      approval.answering = false;
      this.conversationHistory = [...this.conversationHistory];
    }
  }

  private async answerApproval(approval: HitlApprovalInfo, approved: boolean, apiKey?: string): Promise<void> {
    // Answering the card holds Tailor's turn — even a card whose turn a Stop
    // ended — so what the user sends meanwhile waits for its reply.
    this.turns.adopt();
    // Saving a key and signing in take a while; Stop and New chat abort this.
    this.isLoading = true;
    const abortController = new AbortController();
    const signal = abortController.signal;
    this.abortController = abortController;

    // For API key approvals, submit the key before resuming.
    if (approval.type === 'api_key' && approved && approval.oauthConnectionId && apiKey) {
      try {
        await this.externalToolProvidersService.submitUserApiKey(approval.oauthConnectionId, apiKey);
        signal.throwIfAborted();
      } catch (error) {
        console.error('Failed to store API key', error);
        if (this.abortController === abortController) {
          this.isLoading = false;
          this.abortController = undefined;
        }

        // The card stays up to try again, and its turn keeps the slot — unless
        // the turn was stopped or its chat replaced: then the slot passes on.
        this.handOffUnlessBusy();
        return;
      }
    }

    // For OAuth approvals, open the OAuth popup before resuming.
    // Open the popup synchronously (before any async calls) to avoid popup blockers.
    if (approval.type === 'oauth' && approved && approval.oauthConnectionId) {
      const popup = window.open('about:blank', '_blank', 'width=600,height=700');

      try {
        const redirectUri = `${window.location.origin}/api/external-tool-provider/oauth/callback`;
        const { authorizationUrl } = await this.externalToolProvidersService.initiateOAuth(
          approval.oauthConnectionId,
          redirectUri,
        );

        signal.throwIfAborted();

        if (popup) {
          popup.location.href = authorizationUrl;
        }

        // Cancel must close the popup and leave this approval pending.
        await new Promise<void>((resolve, reject) => {
          const onAbort = () => {
            popup?.close();
            this.clearOAuthPopupPoll();
            reject(signal.reason);
          };
          signal.addEventListener('abort', onAbort, { once: true });
          this.oauthPopupPollInterval = setInterval(() => {
            if (!popup || popup.closed) {
              this.clearOAuthPopupPoll();
              signal.removeEventListener('abort', onAbort);
              resolve();
            }
          }, 500);
        });
        signal.throwIfAborted();
      } catch (error) {
        console.error('Failed to initiate OAuth', error);
        popup?.close();
        this.clearOAuthPopupPoll();
        if (this.abortController === abortController) {
          this.isLoading = false;
          this.abortController = undefined;
        }

        // As for the key: the card stays, and so does the slot unless the turn is gone.
        this.handOffUnlessBusy();
        return;
      }
    }

    // Send resume request — this triggers a new streaming response
    this.turns.resumed();
    this.streamingMessage = {
      role: 'assistant',
      content: '',
      timestamp: new Date(),
      metadata: {
        thinkingContent: '',
        contentParts: [],
        isStreaming: true,
      },
    };
    this.thinkingStartTime = null;
    this.toolCallCounter = 0;

    signal.throwIfAborted();
    const hiddenToolCalls = this.getHiddenToolCalls();

    const baseCallbacks: BaseStreamCallbacks<unknown> = {
      onThinking: (content) => {
        if (!this.streamingMessage) {
          return;
        }

        if (this.thinkingStartTime === null) {
          this.thinkingStartTime = Date.now();
        }

        this.thinkingBuffer += content;
        this.scheduleStreamingFlush();
      },
      onThinkingDone: () => {
        this.flushStreamingBuffers();
        if (!this.streamingMessage || this.thinkingStartTime === null) {
          return;
        }

        const dur = Date.now() - this.thinkingStartTime;
        this.streamingMessage = {
          ...this.streamingMessage,
          metadata: { ...this.streamingMessage.metadata, thinkingDurationMs: dur },
        };
        this.thinkingStartTime = null;
      },
      onText: (content) => {
        if (!this.streamingMessage) {
          return;
        }

        this.textBuffer += content;
        this.scheduleStreamingFlush();
      },
      onToolCall: (toolName, input) => {
        this.flushStreamingBuffers();
        if (!this.streamingMessage) {
          return;
        }

        this.onToolCallHook(toolName, input);
        if (hiddenToolCalls.has(toolName)) {
          const parts = [...((this.streamingMessage.metadata?.contentParts as ContentPart[]) || [])];
          const lastPart = parts[parts.length - 1];
          if (lastPart && lastPart.type === 'text' && lastPart.text.trim()) {
            parts.push({ type: 'text', text: '' });
            this.streamingMessage = {
              ...this.streamingMessage,
              metadata: { ...this.streamingMessage.metadata, contentParts: parts },
            };
          }
          return;
        }
        const tc = { id: `tool-${this.toolCallCounter++}`, toolName, input, status: 'pending' as const };
        const parts = [...((this.streamingMessage.metadata?.contentParts as ContentPart[]) || [])];
        parts.push({ type: 'tool-call', toolCall: tc });
        this.streamingMessage = {
          ...this.streamingMessage,
          metadata: { ...this.streamingMessage.metadata, contentParts: parts },
        };
      },
      onToolResult: (toolName) => {
        if (!this.streamingMessage || hiddenToolCalls.has(toolName)) {
          return;
        }

        let found = false;
        const updated = ((this.streamingMessage.metadata?.contentParts as ContentPart[]) || []).map((part) => {
          if (
            part.type === 'tool-call' &&
            !found &&
            part.toolCall.toolName === toolName &&
            part.toolCall.status === 'pending'
          ) {
            found = true;
            return { type: 'tool-call' as const, toolCall: { ...part.toolCall, status: 'complete' as const } };
          }
          return part;
        });
        this.streamingMessage = {
          ...this.streamingMessage,
          metadata: { ...this.streamingMessage.metadata, contentParts: updated },
        };
      },
      onStepStart: () => {},
      onStepFinish: () => {
        this.flushStreamingBuffers();
        if (this.streamingMessage?.content.trim()) {
          this.streamingMessage = { ...this.streamingMessage, content: this.streamingMessage.content + '\n\n' };
        }
      },
      onDone: (response: unknown) => {
        approval.resolved = true;
        approval.resolution = approved ? 'approved' : 'rejected';
        this.pendingHitlApproval = false;
        this.conversationHistory = [...this.conversationHistory];
        this.flushStreamingBuffers();
        const responseObj = response as { sessionId?: string; message?: string };
        this.sessionId = responseObj.sessionId;
        if (this.streamingMessage) {
          const finalMessage: CopilotMessage = {
            role: 'assistant',
            content: responseObj.message ?? '',
            timestamp: this.streamingMessage.timestamp,
            thinkingContent: this.streamingMessage.metadata?.thinkingContent as string,
            thinkingDurationMs:
              (this.streamingMessage.metadata?.thinkingDurationMs as number) ??
              (this.thinkingStartTime !== null ? Date.now() - this.thinkingStartTime : undefined),
            contentParts: this.streamingMessage.metadata?.contentParts as ContentPart[],
            isStreaming: false,
          };
          if (finalMessage.contentParts?.length) {
            finalMessage.contentParts = finalMessage.contentParts.map((part) => {
              if (part.type === 'tool-call' && part.toolCall.status === 'pending') {
                return { type: 'tool-call' as const, toolCall: { ...part.toolCall, status: 'complete' as const } };
              }
              return part;
            });
          }
          this.conversationHistory = [...this.conversationHistory, finalMessage];
        }
        this.streamingMessage = null;
        this.thinkingStartTime = null;
        this.onStreamDone(response);
      },
      onError: (errorMessage) => {
        this.cancelStreamingFlush();
        console.error('HITL resume streaming error:', errorMessage);
        this.sessionUnavailable = true;
        this.conversationHistory = [
          ...this.conversationHistory,
          {
            role: 'assistant',
            content: UNCONFIRMED_APPROVAL_MESSAGE,
            timestamp: new Date(),
            isError: true,
          } as CopilotMessage,
        ];
        this.streamingMessage = null;
        this.thinkingStartTime = null;
      },
    };

    const copilotCallbacks = this.buildCopilotCallbacks(baseCallbacks);
    const invalidateApproval = () => {
      if (approval.resolved) {
        return;
      }

      approval.invalidated = true;

      if (this.abortController === abortController) {
        this.pendingHitlApproval = false;
        this.cancelStreamingFlush();
        this.streamingMessage = null;
        this.thinkingStartTime = null;
        this.conversationHistory = [...this.conversationHistory];
      }
    };

    // Stopping the response cannot recall a resume already sent to the server.
    signal.addEventListener('abort', invalidateApproval, { once: true });
    // The turn passes on once what it started has stopped. A key save or a
    // sign-in setup stops only when it returns, and its own cleanup hands
    // over; the reply stops at Stop, so hand over then rather than when its
    // cleanup gets round to it.
    signal.addEventListener('abort', () => queueMicrotask(() => this.handOffUnlessBusy()), { once: true });

    try {
      await this.copilotService.resumeFromHitl(
        {
          sessionId: this.sessionId,
          runId: approval.runId,
          type: approval.type === 'tool_approval' ? 'tool_approval_result' : 'oauth_result',
          approved,
          approvalId: approval.approvalId,
          pageContext: this.copilotContext.getContext(),
          activePageIds: [...this.copilotContext.activePageIds],
          activeContexts: this.copilotContext.getAllActiveContexts(),
          currentUrl: window.location.pathname,
        },
        copilotCallbacks,
        signal,
      );
    } catch (error: unknown) {
      const err = error as Error;

      if (err?.name === 'AbortError') {
        return;
      }

      console.error('HITL resume request failed:', error);
      this.sessionUnavailable = true;
      this.conversationHistory = [
        ...this.conversationHistory,
        {
          role: 'assistant',
          content:
            (error as Error & { code?: string })?.code === 'copilot_session_unavailable'
              ? err.message
              : UNCONFIRMED_APPROVAL_MESSAGE,
          timestamp: new Date(),
          isError: true,
        } as CopilotMessage,
      ];
      this.streamingMessage = null;
      this.thinkingStartTime = null;
    } finally {
      signal.removeEventListener('abort', invalidateApproval);
      invalidateApproval();

      if (this.abortController === abortController) {
        this.isLoading = false;
        this.abortController = undefined;
      }

      // A resumed turn ends like any other: its continuation, or the user's queued message, goes next.
      this.handOffUnlessBusy();
    }
  }

  // ============================================================================
  // Streaming Request
  // ============================================================================

  protected async performStreamingRequest(
    message: string,
    history: Array<{ role: 'user' | 'assistant'; content: string; timestamp?: Date }>,
    _images: string[] | undefined,
    callbacks: BaseStreamCallbacks<unknown>,
    signal: AbortSignal,
  ): Promise<void> {
    const pageContext = this.copilotContext.getContext();
    const activeContexts = this.copilotContext.getAllActiveContexts();

    this.presentedNotificationIds = presentedNotificationIds(activeContexts);
    this.analyticsService.trackEvent('Tailor Copilot', 'Ask Tailor', { query: message });

    // Generate a session ID on first message; reuse across the conversation.
    // The base class updates this.sessionId from the done payload and resets on clear.
    if (!this.sessionId) {
      this.sessionId = crypto.randomUUID();
    }

    const copilotCallbacks = this.buildCopilotCallbacks(callbacks);

    // The current request is sent separately from prior conversation history.
    // Append it here because the Copilot API accepts one combined messages list.
    const historyMessages = history.map((m) => ({ role: m.role, content: m.content }));
    const lastMsg = historyMessages[historyMessages.length - 1];
    const isMessageInHistory = lastMsg?.content === message && lastMsg?.role === 'user';

    await this.copilotService.sendMessageStreaming(
      {
        messages: isMessageInHistory
          ? historyMessages
          : [...historyMessages, { role: 'user' as const, content: message }],
        pageContext,
        activePageIds: [...this.copilotContext.activePageIds],
        activeContexts,
        sessionId: this.sessionId,
        currentUrl: this.router.url,
      },
      copilotCallbacks,
      signal,
    );
  }

  // ============================================================================
  // Copilot Callback Builder
  // ============================================================================

  private buildCopilotCallbacks(baseCallbacks: BaseStreamCallbacks<unknown>): CopilotStreamCallbacks {
    let rewriteFeedback: string | undefined;
    let stopDraftWrites = false;

    const reportRewriteResult = (message: string, stopWrites: boolean) => {
      rewriteFeedback = message;
      stopDraftWrites = stopWrites;
      this.textBuffer = '';

      if (this.streamingMessage) {
        this.streamingMessage = {
          ...this.streamingMessage,
          content: '',
          metadata: {
            ...this.streamingMessage.metadata,
            contentParts: ((this.streamingMessage.metadata?.contentParts as ContentPart[]) || []).filter(
              (part) => part.type !== 'text',
            ),
          },
        };
      }
    };

    return {
      ...baseCallbacks,
      onText: (content) => {
        if (!rewriteFeedback) {
          baseCallbacks.onText?.(content);
        }
      },
      onDone: (response) => {
        if (rewriteFeedback) {
          baseCallbacks.onText?.(rewriteFeedback);
        }

        baseCallbacks.onDone?.(rewriteFeedback ? { ...response, message: rewriteFeedback } : response);
      },
      onSubAgentStart: (subAgent) => this.addSubAgentPart(subAgent),
      onSubAgentThinking: (subAgent, content) =>
        this.updateSubAgentPart(subAgent, (p) => {
          p.thinkingContent = (p.thinkingContent || '') + content;
        }),
      onSubAgentText: (subAgent, content) =>
        this.updateSubAgentPart(subAgent, (p) => {
          p.textContent = (p.textContent || '') + content;
        }),
      onSubAgentToolCall: (subAgent, toolName, input) =>
        this.updateSubAgentPart(subAgent, (p) => {
          p.toolCalls.push({ id: `sub-tool-${this.toolCallCounter++}`, toolName, input, status: 'pending' });
        }),
      onSubAgentToolResult: (subAgent, toolName) =>
        this.updateSubAgentPart(subAgent, (p) => {
          const tc = p.toolCalls.find((t) => t.toolName === toolName && t.status === 'pending');

          if (tc) {
            tc.status = 'complete';
          }
        }),
      onSubAgentDone: (subAgent) =>
        this.updateSubAgentPart(subAgent, (p) => {
          p.status = 'done';
        }),
      onSubAgentError: (subAgent) =>
        this.updateSubAgentPart(subAgent, (p) => {
          p.status = 'error';
        }),
      onPageAction: (_type, data) => {
        // BE → FE actions arrive via `invoke-fe-tool` with `{ name, args }`.
        // CopilotContextService dispatches to the active page
        // provider (or app-wide handlers like `navigate`). A false return means
        // the precondition wasn't met (composer not ready, no selection, etc.)
        // — surface that to the user, since the in-app copilot panel doesn't
        // have a back-channel to tell the agent the action was dropped.
        if (typeof data['name'] === 'string') {
          const name = data['name'];
          const args = (data['args'] ?? {}) as Record<string, unknown>;
          // The turn that sent this action: only it can be continued if the page refuses.
          const turnId = this.turns.currentTurnId;

          if (name === 'insert_draft_response') {
            if (stopDraftWrites) {
              return;
            }

            if (args['replace']) {
              // Reply replacements are synchronous; use the editor result before displaying the agent's confirmation.
              try {
                const applied = this.copilotContext.executeAction(name, args);
                reportRewriteResult(
                  applied
                    ? 'Updated the reply draft. Review it before sending.'
                    : "I couldn't apply the rewrite. Review the current draft and try again.",
                  !applied,
                );
              } catch (error) {
                console.error('Failed to confirm reply rewrite:', error);
                reportRewriteResult(
                  "I couldn't confirm whether the rewrite was applied. Review the current draft before trying again.",
                  true,
                );
              }

              return;
            }
          }

          void this.copilotContext
            .executeActionForOutcome(name, args)
            .then(({ applied, reason }) => {
              if (applied) {
                return;
              }

              // A page that says why gets the reason to Tailor, in a
              // continuation of the turn that sent the action — unless that
              // turn is over, was stopped, or is itself the continuation; then
              // the user hears it directly.
              if (reason && this.turns.refused(turnId, reason) === 'continue') {
                return;
              }

              // The reason is worded for Tailor, so the user gets plain words.
              this.messageSvc.show(
                reason
                  ? "Tailor's change couldn't be applied because the page changed while it worked. Ask Tailor to try again."
                  : `Couldn't apply ${name} — the page isn't ready or required state is missing.`,
                { color: 'warning' },
              );
            })
            .catch((error) => {
              console.error(`Failed to apply ${name}:`, error);
              this.messageSvc.show(`Couldn't apply ${name} — the action failed.`, { color: 'warning' });
            });
        }
      },
    };
  }

  // ============================================================================
  // Sub-Agent Content Part Helpers
  // ============================================================================

  private addSubAgentPart(subAgent: string): void {
    // Flush any buffered text before adding the sub-agent part
    this.flushStreamingBuffers();

    if (!this.streamingMessage) {
      return;
    }

    const parts = [...((this.streamingMessage.metadata?.contentParts as ContentPart[]) || [])];

    parts.push({
      type: 'sub-agent',
      subAgent: {
        name: subAgent,
        label: this.getSubAgentLabel(subAgent),
        status: 'running',
        toolCalls: [],
      },
    });

    this.streamingMessage = {
      ...this.streamingMessage,
      metadata: { ...this.streamingMessage.metadata, contentParts: parts },
    };
  }

  private updateSubAgentPart(subAgent: string, updater: (part: SubAgentProgress) => void): void {
    if (!this.streamingMessage) {
      return;
    }

    const parts = [...((this.streamingMessage.metadata?.contentParts as ContentPart[]) || [])];

    // Find the last sub-agent part matching this name
    for (let i = parts.length - 1; i >= 0; i--) {
      const part = parts[i];

      if (part.type === 'sub-agent' && part.subAgent.name === subAgent) {
        const updated = { ...part.subAgent, toolCalls: [...part.subAgent.toolCalls] };
        updater(updated);
        parts[i] = { type: 'sub-agent', subAgent: updated };
        break;
      }
    }

    this.streamingMessage = {
      ...this.streamingMessage,
      metadata: { ...this.streamingMessage.metadata, contentParts: parts },
    };
  }

  private getSubAgentLabel(subAgent: string): string {
    switch (subAgent) {
      case 'filter':
        return 'Updating filters';
      case 'automation':
        return 'Modifying automation';
      case 'rewrite':
        return 'Rewriting text';
      case 'kb_article_edit':
        return 'Editing article';
      case 'code':
        return 'Writing code';
      case 'analytics':
        return 'Analyzing data';
      case 'navigation':
        return 'Finding page';
      default:
        return 'Processing';
    }
  }

  // ============================================================================
  // Lifecycle
  // ============================================================================

  ngOnInit(): void {
    this.contextDisplaySub = this.copilotContext.contextDisplay$.subscribe((context) => {
      this.contextDisplayContext = context;
      this.refreshEmptyStatePresets();
    });
    this.notificationsSub = this.copilotNotifications.unseenInView$.subscribe((unseen) => {
      this.unseenNotificationIds = unseen.map((notification) => notification.id);
      this.startIdleTurn();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isOpen']?.currentValue === true && !changes['isOpen']?.previousValue) {
      setTimeout(() => this.chatPanel?.focusInput());
    }

    if (changes['isOpen']) {
      const open = Boolean(changes['isOpen'].currentValue);

      // Pages' background checks pause while Tailor is open.
      this.copilotContext.setPanelOpen(open);
      this.turns.panelOpenChanged(open);

      if (open && this.presentedWhileClosed.length) {
        this.copilotNotifications.markSeen(this.presentedWhileClosed);
        this.presentedWhileClosed = [];
      }

      // After the open has rendered, so the hidden turn lands in a visible panel.
      setTimeout(() => this.startIdleTurn());
    }
  }

  /** A new chat is a clean break: a reply still in progress is stopped first, as if the user pressed Stop. */
  override onNewChat(): void {
    if (this.isLoading) {
      this.onCancelRequest();
    }

    super.onNewChat();
    setTimeout(() => {
      this.chatPanel?.focusInput();
      // A fresh chat brings up what Copilot noticed, as opening the panel does. A stopped
      // reply still finishing keeps Tailor busy, and its cleanup asks again.
      this.startIdleTurn();
    });
  }

  ngOnDestroy(): void {
    this.contextDisplaySub?.unsubscribe();
    this.notificationsSub?.unsubscribe();
    this.clearOAuthPopupPoll();
    this.baseOnDestroy();
  }

  private refreshEmptyStatePresets(): void {
    this.emptyState = {
      ...this.emptyState,
      presets: getCopilotPresetsForContext(this.contextDisplayContext, { cursorInsertPresetsEnabled: true }),
    };
  }

  /**
   * An approval click settled. If Stop or New chat cut it short, a later
   * request may already be running: that one passes the slot on when it ends.
   */
  private handOffUnlessBusy(): void {
    if (!this.isLoading) {
      this.startNextTurn();
    }
  }

  private clearOAuthPopupPoll(): void {
    if (this.oauthPopupPollInterval) {
      clearInterval(this.oauthPopupPollInterval);
      this.oauthPopupPollInterval = null;
    }
  }

  // ============================================================================
  // Message Action Handling
  // ============================================================================

  onMessageActionClick(event: MessageActionClickEvent): void {
    switch (event.action.id) {
      case 'new_chat':
        this.onNewChat();
        break;
      case 'retry':
        this.onRetryLastMessage();
        break;
      case 'resend':
        this.onResendLastMessage();
        break;
      case 'delete_message':
        this.onDeleteLastMessage();
        break;
    }
  }
}
